// src/hooks/useOfflineSalesForm.js
//
// Manual/credit-sale-oriented Offline Sales order form hook.
// Grounded in usePosTerminal's cart-management naming style (addToCart/updateCartItem/
// removeFromCart become addItem/updateItem/removeItem here) and useOrderForm's
// errors/validateForm computed-state pattern.
//
// Key difference from the POS terminal (usePosTerminal / useOfflineSalesTerminal):
// this hook is for the manual Create Offline Sales Order page, where:
//   - paymentDetails are allowed to sum to LESS than grandTotal (credit sales), unlike
//     the terminal which requires full payment before completing.
//   - immediateDelivery defaults to false (deferred delivery via an explicit Delivery
//     Note is the norm here) — the terminal already covers the always-immediate,
//     fully-paid counter-sale path via its own hook.
//
// Payload shape is grounded directly in
// rettzy-backend/src/modules/offlineSales/services/offlineOrder.service.js `createOrder()`
// and its Joi schema (src/modules/offlineSales/validations/order.validation.js).
import { useState, useCallback, useEffect } from 'react';
import { createOrder, updateOrder } from '../services/api/offlineSales/orders';
import { useAlert } from '../contexts/AlertContext';
import useDeliveryChargeCalculation from './useDeliveryChargeCalculation';
import { getEffectiveLineDiscount } from '../utils/orderCalculations';
import { emptyCheque, getChequeErrorKey, toChequePayload } from '../components/common/ChequeDetailsFields/ChequeDetailsFields';

const FULFILLMENT_METHODS = ['counter_pickup', 'self_delivery', 'courier'];

const makeLocalId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// Pass an already-loaded order to hydrate the form for editing (EditOfflineSalesOrderPage) —
// omit/pass null for the create flow (CreateOfflineSalesOrderPage), matching useOrderForm.js's
// (initialData, options) convention.
// Existing payment's cheque → form state. Cheques no longer in hand (deposited onwards, or
// part of a customer receipt) are locked against edits.
const toFormCheque = (cheque) => {
  const value = cheque
    ? { isPostDated: Boolean(cheque.isPostDated), chequeDate: cheque.isPostDated ? String(cheque.chequeDate || '').slice(0, 10) : '' }
    : emptyCheque();
  return {
    cheque: value,
    originalCheque: value,
    chequeLocked: Boolean(cheque && (cheque.status !== 'pending' || cheque.customerPaymentId)),
  };
};

const isChequeChanged = (p) => !p.originalCheque
  || Boolean(p.cheque?.isPostDated) !== Boolean(p.originalCheque.isPostDated)
  || (p.cheque?.chequeDate || '') !== (p.originalCheque.chequeDate || '');

const useOfflineSalesForm = (initialOrder = null) => {
  const { error: showError } = useAlert();

  // ─── Shop context ───
  // Not part of the approved state shape's headline list, but required by the backend
  // (shopId is Joi-required on createOrder) — every other field in this module is scoped
  // to a shop, so the page must be able to set it (e.g. via a shop selector, mirroring
  // CreateOrderPage's ShopSelectionSection).
  const [shopId, setShopId] = useState(null);

  // ─── Core state ───
  const [customer, setCustomer] = useState(null); // null = Cash Sales (backend resolves it)
  const [items, setItems] = useState([]);
  const [warehouseId, setWarehouseId] = useState(null);
  const [fulfillmentMethod, setFulfillmentMethod] = useState('counter_pickup');
  // Only meaningful when fulfillmentMethod === 'courier' — delivery city + package weight,
  // feeding the shared delivery-charge calculation (Stage 8).
  const [cityId, setCityId] = useState(null);
  const [weight, setWeight] = useState(1);
  // Delivery address (courier fulfillment only) — entered/edited per-order in the Delivery
  // Details section, defaulting to the selected customer's stored address but user-editable.
  const [address, setAddress] = useState('');
  // A manual override for the auto-calculated delivery charge (courier fulfillment only),
  // for users with the `change_delivery_charge` permission — null means "use the calculated
  // value", mirroring the online order form's editable delivery-charge field.
  const [deliveryChargeOverride, setDeliveryChargeOverride] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [orderDiscount, setOrderDiscount] = useState(0);
  const [notes, setNotes] = useState('');
  const [salesRepUserId, setSalesRepUserId] = useState(null);

  // ─── UI state ───
  const [errors, setErrors] = useState({});
  const [creditLimitWarning, setCreditLimitWarning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [completedOrder, setCompletedOrder] = useState(null);

  // ─── Hydrate from an existing order (edit mode) ───
  useEffect(() => {
    if (!initialOrder) return;
    setShopId(initialOrder.shopId || null);
    setCustomer(initialOrder.customer || null);
    setItems((initialOrder.orderItems || []).map(i => ({
      key: `${i.productId}-${i.variationId || 'null'}`,
      productId: i.productId,
      variationId: i.variationId || null,
      name: i.Product?.name + (i.ProductVariation?.sku ? ` — ${i.ProductVariation.sku}` : ''),
      unitPrice: parseFloat(i.unitPrice),
      quantity: parseFloat(i.quantity),
      // Form state keeps the manual discount; free-unit value is added on top (see getEffectiveLineDiscount).
      lineDiscount: parseFloat(i.manualDiscount ?? i.lineDiscount) || 0,
      freeQuantity: parseInt(i.freeQuantity, 10) || 0,
    })));
    setWarehouseId(initialOrder.warehouseId || null);
    setFulfillmentMethod(initialOrder.fulfillmentMethod || 'counter_pickup');
    setAddress(initialOrder.address || '');
    setCityId(initialOrder.cityId || null);
    setWeight(initialOrder.weight || 1);
    setDeliveryChargeOverride(initialOrder.fulfillmentMethod === 'courier' ? parseFloat(initialOrder.deliveryCharge) || 0 : null);
    setOrderDiscount(parseFloat(initialOrder.orderDiscount) || 0);
    setNotes(initialOrder.specialNotes || initialOrder.notes || '');
    setSalesRepUserId(initialOrder.salesRepUserId || null);
    setPaymentDetails((initialOrder.paymentRecords || []).map(r => ({
      id: r.id,
      isExisting: true,
      isVerified: !!r.isVerified,
      paymentMethodKey: r.PaymentMethod?.key || '',
      amount: parseFloat(r.paymentAmount) || 0,
      paymentDate: r.paymentDate || '',
      reference: r.reference || '',
      proofUrl: r.proofUrl || null,
      ...toFormCheque(r.cheque),
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOrder?.id]);

  // ─── Item helpers (usePosTerminal's addToCart/updateCartItem/removeFromCart naming,
  // renamed per the approved blueprint to addItem/updateItem/removeItem) ───
  const addItem = useCallback((product, variationId = null, variationName = null) => {
    setItems(prev => {
      const key = `${product.productId}-${variationId || 'null'}`;
      const existing = prev.find(i => i.key === key);
      if (existing) {
        return prev.map(i => (i.key === key ? { ...i, quantity: i.quantity + 1 } : i));
      }
      return [
        ...prev,
        {
          key,
          productId: product.productId,
          variationId: variationId || null,
          name: product.displayName + (variationName ? ` — ${variationName}` : ''),
          unitPrice: parseFloat(product.price || 0),
          quantity: 1,
          lineDiscount: 0,
          freeQuantity: 0,
        },
      ];
    });
  }, []);

  const updateItem = useCallback((key, field, value) => {
    setItems(prev =>
      prev.map(i => {
        if (i.key !== key) return i;
        if (field === 'quantity') {
          const quantity = parseFloat(value) || 0;
          return { ...i, quantity, freeQuantity: Math.min(i.freeQuantity || 0, Math.max(0, quantity - 1)) };
        }
        if (field === 'freeQuantity') {
          return { ...i, freeQuantity: Math.min(Math.max(0, parseInt(value, 10) || 0), Math.max(0, i.quantity - 1)) };
        }
        if (field === 'unitPrice' || field === 'lineDiscount') {
          return { ...i, [field]: parseFloat(value) || 0 };
        }
        return { ...i, [field]: value };
      })
    );
  }, []);

  const removeItem = useCallback((key) => {
    setItems(prev => prev.filter(i => i.key !== key));
  }, []);

  // ─── Delivery charge (courier fulfillment only) ───
  // Reuses the exact same shared calculation engine as the online order form (Stage 8) — no
  // offers/shop-override concept here, so resolveOverride is omitted.
  const { deliveryCharge, isCalculatingDelivery } = useDeliveryChargeCalculation({
    shopId,
    cityId,
    weight,
    orderItems: items,
    disableAutoCalc: fulfillmentMethod !== 'courier',
  });

  // ─── Totals ───
  // Tax preview mirrors the backend's per-line calc (validateOrderItems in
  // offlineOrder.service.js): discountedLineAmount * shop.taxPercentage, when the
  // selected shop is taxable. Pass the selected shop object in so the preview matches
  // what the backend will actually charge; omit it (or pass a non-taxable shop) and tax
  // previews as 0 — the server remains the source of truth regardless.
  const computeTotals = useCallback((shop = null) => {
    let subtotal = 0;
    let itemDiscounts = 0;
    let tax = 0;
    const taxRate = shop?.isTaxable ? (parseFloat(shop.taxPercentage) || 0) / 100 : 0;

    items.forEach(i => {
      const lineSubtotal = i.quantity * i.unitPrice;
      const lineDiscount = getEffectiveLineDiscount(i);
      subtotal += lineSubtotal;
      itemDiscounts += lineDiscount;
      tax += taxRate * (lineSubtotal - lineDiscount);
    });

    const discount = itemDiscounts + (parseFloat(orderDiscount) || 0);
    // Delivery charge only applies to courier-fulfilled orders, mirroring the backend's
    // grandTotal formula (offlineOrder.service.js:createOrder). A manual override (when set
    // by a user with change_delivery_charge) takes priority over the calculated value.
    const effectiveDeliveryCharge = fulfillmentMethod === 'courier'
      ? (deliveryChargeOverride != null ? parseFloat(deliveryChargeOverride) || 0 : parseFloat(deliveryCharge) || 0)
      : 0;
    const grandTotal = Math.max(0, subtotal - discount + tax + effectiveDeliveryCharge);
    const paidAmount = paymentDetails.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
    const balanceAmount = Math.max(0, grandTotal - paidAmount);

    return { subtotal, discount, tax, deliveryCharge: effectiveDeliveryCharge, grandTotal, paidAmount, balanceAmount };
  }, [items, orderDiscount, paymentDetails, fulfillmentMethod, deliveryCharge, deliveryChargeOverride]);

  const totals = computeTotals();

  // ─── Payment detail helpers ───
  // Unlike the POS terminal, these are allowed to sum to less than the grand total —
  // a partial/credit sale is a normal outcome here, not an error.
  const addPaymentDetail = useCallback((paymentMethodKey = '') => {
    const { balanceAmount } = computeTotals();
    setPaymentDetails(prev => [
      ...prev,
      {
        id: makeLocalId(),
        paymentMethodKey,
        amount: balanceAmount > 0 ? balanceAmount : '',
        paymentDate: new Date().toISOString().split('T')[0],
        reference: '',
        proofUrl: null,
        cheque: emptyCheque(),
      },
    ]);
  }, [computeTotals]);

  const updatePaymentDetail = useCallback((id, field, value) => {
    setPaymentDetails(prev =>
      prev.map(p => {
        if (p.id !== id) return p;
        if (field === 'amount') return { ...p, amount: parseFloat(value) || 0 };
        return { ...p, [field]: value };
      })
    );
  }, []);

  const removePaymentDetail = useCallback((id) => {
    setPaymentDetails(prev => prev.filter(p => p.id !== id));
  }, []);

  // ─── Validation (useOrderForm's errors/validateForm computed-state pattern) ───
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!shopId) newErrors.shopId = 'Shop selection is required';

    if (!FULFILLMENT_METHODS.includes(fulfillmentMethod)) {
      newErrors.fulfillmentMethod = 'Invalid fulfillment method';
    }

    if (fulfillmentMethod === 'courier' && !cityId) {
      newErrors.cityId = 'Delivery city is required for courier fulfillment';
    }

    if (fulfillmentMethod === 'courier') {
      if (!customer) newErrors.customer = 'Courier delivery requires a specific customer, not Cash Sales';
      if (!address || !address.trim()) newErrors.address = 'Delivery address is required for courier orders';
    }

    if (items.length === 0) {
      newErrors.items = 'At least one item is required';
    } else {
      const itemErrors = [];
      items.forEach((item, index) => {
        const itemError = {};
        if (!item.quantity || item.quantity <= 0) itemError.quantity = 'Quantity must be greater than 0';
        if (item.unitPrice == null || item.unitPrice < 0) itemError.unitPrice = 'Unit price cannot be negative';
        if (item.lineDiscount && item.lineDiscount < 0) itemError.lineDiscount = 'Discount cannot be negative';
        const freeQuantity = item.freeQuantity || 0;
        if (!Number.isInteger(freeQuantity) || freeQuantity < 0 || (freeQuantity > 0 && freeQuantity >= item.quantity)) {
          itemError.freeQuantity = 'Free units must be less than quantity';
        }
        if (!itemError.lineDiscount && getEffectiveLineDiscount(item) - item.quantity * item.unitPrice > 0.005) {
          itemError.lineDiscount = 'Total discount (incl. free units) cannot exceed the line subtotal';
        }
        if (Object.keys(itemError).length > 0) itemErrors[index] = itemError;
      });
      if (itemErrors.length > 0) newErrors.itemDetails = itemErrors;
    }

    if (orderDiscount < 0) newErrors.orderDiscount = 'Order discount cannot be negative';

    if (paymentDetails.length > 0) {
      const paymentErrors = [];
      paymentDetails.forEach((p, index) => {
        const pError = {};
        if (!p.paymentMethodKey) pError.paymentMethodKey = 'Payment method is required';
        if (!p.amount || p.amount <= 0) pError.amount = 'Amount must be greater than 0';
        if (p.paymentMethodKey === 'cheque' && !p.chequeLocked && isChequeChanged(p) && getChequeErrorKey(p.cheque)) pError.cheque = getChequeErrorKey(p.cheque);
        if (Object.keys(pError).length > 0) paymentErrors[index] = pError;
      });
      if (paymentErrors.length > 0) newErrors.paymentDetails = paymentErrors;

      // Unlike the terminal, overpayment (not underpayment) is the only hard error here —
      // partial/credit payment is expected and handled server-side via offline_credit status.
      const { grandTotal, paidAmount } = computeTotals();
      if (paidAmount - grandTotal > 0.01) {
        newErrors.paymentDetails = 'Total paid cannot exceed the grand total';
      }
    }

    // Cash Sales customer requires full payment — courier orders are exempt (an unpaid
    // balance there is a normal COD case, not a credit sale; mirrors the backend guard in
    // offlineOrder.service.js::createOrder).
    if (!customer && fulfillmentMethod !== 'courier' && computeTotals().balanceAmount > 0) {
      newErrors.creditSale = 'Credit sales are not allowed for Cash Sales customer — full payment is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [shopId, fulfillmentMethod, cityId, customer, address, items, orderDiscount, paymentDetails, computeTotals]);

  // ─── Submit ───
  // immediateDelivery: exposed default is false (manual/credit-sale-oriented form).
  // The caller (CreateOfflineSalesOrderPage) may explicitly request it via this option —
  // it is forced back to false server-side anyway when fulfillmentMethod === 'courier'
  // (courier stock deduction happens at packing-batch dispatch time, never inline).
  const submitOrder = useCallback(async ({
    sessionId = null,
    orderStatusKey,
    immediateDelivery = false,
  } = {}) => {
    if (!validateForm()) return { success: false };

    // Courier-fulfilled offline orders follow the online order lifecycle (pending -> packing ->
    // dispatch), not the offline counter-sale completed/credit states — 'offline_completed' is
    // invalid for channel='offline' + fulfillmentMethod='courier' per validateOrderStatusForChannel.
    const effectiveOrderStatusKey = orderStatusKey
      || (fulfillmentMethod === 'courier' ? 'pending' : 'offline_completed');

    const effectiveImmediateDelivery = fulfillmentMethod !== 'courier' && immediateDelivery;

    try {
      setSubmitting(true);
      setCreditLimitWarning(false);

      const payload = {
        shopId,
        customerId: customer?.id || null,
        warehouseId: warehouseId || null,
        sessionId,
        orderItems: items.map(i => ({
          productId: i.productId,
          variationId: i.variationId,
          quantity: i.quantity,
          unitPrice: i.unitPrice,
          lineDiscount: parseFloat(i.lineDiscount) || 0,
          freeQuantity: i.freeQuantity || 0,
        })),
        orderDiscount: parseFloat(orderDiscount) || 0,
        paymentDetails: paymentDetails.map(p => ({
          paymentMethodKey: p.paymentMethodKey,
          amount: parseFloat(p.amount) || 0,
          paymentDate: p.paymentDate || null,
          reference: p.reference || null,
          proofUrl: p.proofUrl || null,
          ...(p.paymentMethodKey === 'cheque' ? { cheque: toChequePayload(p.cheque) } : {}),
        })),
        notes: notes || null,
        salesRepUserId: salesRepUserId || null,
        fulfillmentMethod,
        orderStatusKey: effectiveOrderStatusKey,
        immediateDelivery: effectiveImmediateDelivery,
        // Only meaningful for courier fulfillment — backend ignores these otherwise.
        cityId: cityId || null,
        weight: weight || null,
        deliveryCharge: deliveryChargeOverride != null ? parseFloat(deliveryChargeOverride) || 0 : parseFloat(deliveryCharge) || 0,
        address: address || null,
      };

      const response = await createOrder(payload);
      const order = response.order;

      setCompletedOrder(order);
      if (order?.creditLimitExceeded) setCreditLimitWarning(true);

      return { success: true, order };
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to create order';
      showError(message);
      return { success: false, message };
    } finally {
      setSubmitting(false);
    }
  }, [validateForm, shopId, customer, warehouseId, items, orderDiscount, paymentDetails, notes, salesRepUserId, fulfillmentMethod, cityId, weight, deliveryCharge, deliveryChargeOverride, address, showError]);

  // Edit mode submit — full parity with submitOrder: shop/customer/warehouse/fulfillment/
  // delivery details are all editable, matching updateOnHoldOrderSchema's extended field set.
  const editOrder = useCallback(async (orderId) => {
    if (!validateForm()) return { success: false };
    setSubmitting(true);
    try {
      const payload = {
        shopId,
        customerId: customer?.id || null,
        warehouseId: warehouseId || null,
        fulfillmentMethod,
        orderItems: items.map(i => ({
          productId: i.productId,
          variationId: i.variationId,
          quantity: i.quantity,
          unitPrice: i.unitPrice,
          lineDiscount: parseFloat(i.lineDiscount) || 0,
          freeQuantity: i.freeQuantity || 0,
        })),
        orderDiscount: parseFloat(orderDiscount) || 0,
        paymentDetails: paymentDetails.map(p => ({
          id: p.id && typeof p.id === 'number' ? p.id : undefined,
          paymentMethodKey: p.paymentMethodKey,
          amount: parseFloat(p.amount) || 0,
          paymentDate: p.paymentDate || null,
          reference: p.reference || null,
          proofUrl: p.proofUrl || null,
          ...(p.paymentMethodKey === 'cheque' && !p.chequeLocked && isChequeChanged(p) ? { cheque: toChequePayload(p.cheque) } : {}),
        })),
        notes: notes || null,
        salesRepUserId: salesRepUserId || null,
        // Only meaningful for courier fulfillment — backend ignores these otherwise.
        cityId: cityId || null,
        weight: weight || null,
        deliveryCharge: deliveryChargeOverride != null ? parseFloat(deliveryChargeOverride) || 0 : parseFloat(deliveryCharge) || 0,
        address: address || null,
      };
      const response = await updateOrder(orderId, payload);
      return { success: true, order: response.order };
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to update order';
      showError(message);
      return { success: false, message };
    } finally {
      setSubmitting(false);
    }
  }, [validateForm, shopId, customer, warehouseId, fulfillmentMethod, items, orderDiscount, paymentDetails, notes, salesRepUserId, cityId, weight, deliveryCharge, deliveryChargeOverride, address, showError]);

  const resetForm = useCallback(() => {
    setCustomer(null);
    setItems([]);
    setWarehouseId(null);
    setFulfillmentMethod('counter_pickup');
    setCityId(null);
    setWeight(1);
    setAddress('');
    setDeliveryChargeOverride(null);
    setPaymentDetails([]);
    setOrderDiscount(0);
    setNotes('');
    setSalesRepUserId(null);
    setErrors({});
    setCreditLimitWarning(false);
    setCompletedOrder(null);
  }, []);

  return {
    // shop context (see note above)
    shopId, setShopId,
    // core state
    customer, items, warehouseId, fulfillmentMethod, paymentDetails, orderDiscount, notes, salesRepUserId,
    cityId, weight, address, isCalculatingDelivery, deliveryChargeOverride,
    totals, errors, creditLimitWarning, loading, submitting, completedOrder,
    // setters
    setCustomer, setWarehouseId, setFulfillmentMethod, setOrderDiscount, setNotes, setSalesRepUserId, setLoading,
    setCityId, setWeight, setAddress, setDeliveryChargeOverride,
    // item helpers
    addItem, updateItem, removeItem,
    // payment detail helpers
    addPaymentDetail, updatePaymentDetail, removePaymentDetail,
    // computed totals for a specific shop (tax preview)
    computeTotals,
    // validation + submission
    validateForm, submitOrder, editOrder, resetForm,
  };
};

export default useOfflineSalesForm;
